import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  useIncident,
  useIncidentHubEvent,
  useIncidentStreams,
  usePortalHub,
  useRealtimeConnection,
} from '@ngsos/portal-core/portal';

import { NgPageHeader, RealtimeConnectionStatusBanner } from '@/ui';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import { getQueryParamCaseInsensitive } from '@/features/shared/queryParams';
import { CallerConnectedStatus, CollaborationState, IncidentState } from '@/signalr';
import { eedCallCapabilitiesQueryKey, fetchEedCallCapabilities } from './api/incidentDetailApi';
import { openPhoneNumberDialog } from './actions';
import { IncidentDetailDialogs } from './dialogs/IncidentDetailDialogs';
import { createIncidentRealtime, IncidentRealtimeContext } from './hub/incidentRealtime';
import { getPsapView, isEedLiveVideo } from './model/incidentDomain';
import { ActionsMobileMenuProvider } from './layout/actionsMobileMenu';
import { IncidentDetailLayoutBox } from './layout/IncidentDetailLayoutBox';
import { IncidentBanner } from './banners/IncidentBanner';
import { IncidentDetailCollaborationStateBanner } from './banners/IncidentDetailCollaborationStateBanner';
import { IncidentDataPanel } from './IncidentDataPanel';
import { IncidentDetailActionBar } from './IncidentDetailActionBar';
import { IncidentDetailMap } from './IncidentDetailMap';
import {
  IncidentDetailMapLoadingPlaceholder,
  IncidentDetailPanelLoadingPlaceholder,
} from './IncidentDetailLoadingPlaceholders';
import { IncidentChatPlaceholder } from './placeholders/IncidentChatPlaceholder';
import { VideoBoxPlaceholder } from './placeholders/VideoBoxPlaceholder';

export type IncidentDetailPageProps = {
  incidentId: string;
};

export function IncidentDetailPage({ incidentId }: IncidentDetailPageProps) {
  const { t } = useTranslation();
  const psapConfiguration = usePsapConfiguration();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const hub = usePortalHub();
  const connection = useRealtimeConnection();
  const incident = useIncident(incidentId);

  const { isWatching } = useIncidentStreams(incidentId);

  const realtime = useMemo(
    () => (hub === null ? null : createIncidentRealtime(hub, incidentId)),
    [hub, incidentId],
  );

  const callerPhoneNumbers = incident?.caller.phoneNumbers ?? [];

  const eedCallCapabilitiesQuery = useQuery({
    queryKey: eedCallCapabilitiesQueryKey(incidentId, callerPhoneNumbers),
    queryFn: () => fetchEedCallCapabilities(callerPhoneNumbers),
    enabled: incident !== null && callerPhoneNumbers.length > 0,
  });
  const eedCallCapabilities = eedCallCapabilitiesQuery.data ?? [];

  const isLoading = incident === null || eedCallCapabilitiesQuery.isLoading;

  const [callerConnectedStatus, setCallerConnectedStatus] = useState<CallerConnectedStatus>(
    CallerConnectedStatus.NotConnectedYet,
  );
  useIncidentHubEvent(incidentId, 'CallerConnectionStatusChanged', setCallerConnectedStatus);
  useEffect(() => {
    if (realtime === null || !isWatching) {
      return;
    }
    let cancelled = false;
    void realtime
      .getLiveState()
      .then((liveState) => {
        if (!cancelled) {
          setCallerConnectedStatus(liveState.callerConnectedStatus);
        }
      })
      .catch((error: unknown) => {
        console.log('SignalR hub connection was canceled. ' + String(error));
      });
    return () => {
      cancelled = true;
    };
  }, [realtime, isWatching]);

  const state = incident?.state ?? null;
  const hasPhoneNumber = callerPhoneNumbers.length > 0;

  const psapView = incident !== null ? getPsapView(incident, psapConfiguration.id) : null;
  const incidentCollaborationState = psapView?.collaborationState ?? CollaborationState.Supervisor;
  const incidentIsReadOnly =
    state !== IncidentState.Open || incidentCollaborationState === CollaborationState.Rejected;

  const liveVideos = incident?.caller.liveVideos ?? [];
  const activeLiveVideo = liveVideos.find((video) => video.id === incident?.activeLiveVideoId) ?? null;
  const activeEedVideo = isEedLiveVideo(activeLiveVideo) ? activeLiveVideo : null;
  const hasActiveEedVideo = activeEedVideo !== null;
  const hasEedVideoCapability = eedCallCapabilities.some((capability) => capability.hasVideoCapability);
  const eedVideoCallId = activeEedVideo?.callId ?? null;

  const [videoExpanded, setVideoExpanded] = useState(false);

  const phoneNumberDialogHandledRef = useRef(false);
  const openPhoneNumberDialogParam = getQueryParamCaseInsensitive(searchParams, 'OpenPhoneNumberDialog');
  useEffect(() => {
    if (isLoading || phoneNumberDialogHandledRef.current) {
      return;
    }
    if (openPhoneNumberDialogParam?.toLowerCase() === 'true') {
      phoneNumberDialogHandledRef.current = true;
      openPhoneNumberDialog(incidentId);
      void navigate(`/incident/${incidentId}`);
    }
  }, [isLoading, openPhoneNumberDialogParam, incidentId, navigate]);

  return (
    <>
      <NgPageHeader title={t('Incident_PageTitle')} />

      <IncidentRealtimeContext.Provider value={realtime}>
        <ActionsMobileMenuProvider>
          <IncidentDetailLayoutBox
            incidentState={state}
            hasActiveEedVideo={hasActiveEedVideo}
            videoExpanded={videoExpanded}
            dataPanelArea={
              isLoading ? (
                <IncidentDetailPanelLoadingPlaceholder />
              ) : incident !== null ? (
                <IncidentDataPanel
                  incident={incident}
                  incidentIsReadOnly={incidentIsReadOnly}
                  eedCallCapabilities={eedCallCapabilities}
                />
              ) : null
            }
            bannersArea={
              <>
                {state === IncidentState.Closed ? (
                  <IncidentBanner>{t('Incident_IncidentClosedStatusLabel')}</IncidentBanner>
                ) : (
                  incident !== null && (
                    <IncidentDetailCollaborationStateBanner
                      incidentId={incidentId}
                      collaborationState={incidentCollaborationState}
                    />
                  )
                )}

                <RealtimeConnectionStatusBanner connection={connection} />
              </>
            }
            actionBarArea={
              !isLoading && (
                <IncidentDetailActionBar
                  incidentIsReadOnly={incidentIsReadOnly}
                  incidentId={incidentId}
                  lastCallerPosition={incident?.lastPosition ?? null}
                  involvedPsaps={incident?.involvedPsaps}
                />
              )
            }
            mapArea={
              isLoading ? (
                <IncidentDetailMapLoadingPlaceholder />
              ) : (
                <IncidentDetailMap
                  incidentId={incidentId}
                  callerPosition={incident?.lastPosition ?? null}
                  callerPositionHistory={incident?.positions ?? []}
                  permanentResidence={incident?.caller.permanentResidence}
                  incidentIsReadOnly={incidentIsReadOnly}
                />
              )
            }
            videoPanelArea={
              <VideoBoxPlaceholder
                incidentId={incidentId}
                incidentState={state}
                incidentIsReadOnly={incidentIsReadOnly}
                incidentHasPhoneNumber={hasPhoneNumber}
                hasEedVideoCapability={hasEedVideoCapability}
                eedVideoCallId={eedVideoCallId}
                liveVideos={liveVideos}
                activeLiveVideoId={incident?.activeLiveVideoId ?? null}
                videoExpanded={videoExpanded}
                onVideoExpandedChanged={setVideoExpanded}
                callerConnectedStatus={callerConnectedStatus}
              />
            }
            chatArea={
              <IncidentChatPlaceholder
                incidentId={incidentId}
                callerConnectedStatus={callerConnectedStatus}
                incidentHasPhoneNumber={hasPhoneNumber}
                incidentIsReadOnly={incidentIsReadOnly}
                hasActiveEedVideo={hasActiveEedVideo}
                videoExpanded={videoExpanded}
                onRequestVideoCollapse={() => setVideoExpanded(false)}
              />
            }
          />

          <IncidentDetailDialogs
            incidentId={incidentId}
            lastCallerPosition={incident?.lastPosition ?? null}
            involvedPsaps={incident?.involvedPsaps}
          />
        </ActionsMobileMenuProvider>
      </IncidentRealtimeContext.Provider>
    </>
  );
}
